import { Localization } from "OW.Remoting/Remoting/Localization";
import { ErrorHandler } from "UI/Errors";
import { CaseInsensitiveMap } from "../../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { InvalidConditionException } from "../../../../../Global/Base/Exceptions";
import { FieldType, IBaseDynamicSchemaInterfaceClass, IFieldType, ISchemaValidationResult, ObjectSchemaValidator } from "../../../../../Global/Base/Utils/ObjectSchemaValidator";
import { ILocalizedAttributeData } from "../../../../../OW.Remoting/Remoting/DataTypes/IConditionValidation";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../../../../OW.Remoting/Remoting/RequestContext";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { MessageWindow } from "../../MessageWindow";

/**
 * Třída validující uživatelsky vložené podmínky nebo definice filtru
 */
export class FilterValidator {
    /**
     * Zvaliduje data filtru
     * @param filterObject Objekt dat filtru k validaci
     */
    public static validateFilterData<T extends object>(filterObject: IFilterData): ISchemaValidationResult<T>[] {
        const results: ISchemaValidationResult<T>[] = [];

        results.push(FilterValidator._validateObjectSchema(filterObject, IFilterData as object));

        for (const attrData of filterObject.attrs) {
            results.push(FilterValidator._validateObjectSchema(attrData, IFilterAttrData as object));

            for (const attrCondition of attrData.conditions) {
                results.push(FilterValidator._validateObjectSchema(attrCondition, IFilterAttrCondition as object));

                if (attrCondition.values.length > 0) {
                    if (!Array.isArray(attrCondition.values[0]) && typeof (attrCondition.values[0]) === "object") {
                        for (const conditionValue of attrCondition.values) {
                            if (!conditionValue)
                                continue;
                            results.push(FilterValidator._validateObjectSchema(conditionValue as object, IFilterAttrConditionObjectValue as object));
                        }
                    }
                }
            }
        }
        return results;
    }

    private static _validateObjectSchema<T extends object>(object: object, schema: IBaseDynamicSchemaInterfaceClass<T>): ISchemaValidationResult<T> {
        return ObjectSchemaValidator.validateObjectSchema(object, schema, true);
    }

    public static validateColumnDataTypes(filterObject: IFilterData, columnDataTypes: CaseInsensitiveMap<{ dataType: string; locKey: string }>): boolean {
        let success: boolean = true;
        const missingColumns: string[] = [];
        for (const attr of filterObject.attrs) {
            if (!columnDataTypes.get(attr.attrName)) {
                success = false;
                missingColumns.push(attr.attrName);
            }
        }
        if (!success) {
            MessageWindow.showAlert(new LocalizedStringBuilder("GUI-674762", [missingColumns.join(", ")]), "GUI-674376"); //Nelze aplikovat filtr. Sloupec nebo sloupce: '@0' neexistují na aktuálním formuláři. | Vložit filtr
        }
        return success;
    }

    /**
     * Provede validaci podmínky z pokročilého filtru
     * @param condition Podmínka k vyhodnocení
     * @param className Název třídy, nad kterou je podmínka vyhodnocována
     * @param attrNameToDataType Lokalizované názvy atributů tak, jak je vidí uživatel
     * @param requestContext Viz {@link RequestContext}
     */
    public static async validateAdvancedFilterCondition(condition: string, className: string, attrNameToDataType: CaseInsensitiveMap<{ dataType: string; locKey: string }>,
        requestContext?: RequestContext): Promise<string> {
        const attrNameToCaption: ILocalizedAttributeData = {};

        await Promise.all(Array.from(attrNameToDataType, async ([key, data]) => {
            attrNameToCaption[await Localization.getTextPromise(data.locKey)] = key;
        }));

        const result = await OWManager.validateCondition(condition ? condition.replace(/\u00A0/g, " ") : condition, className, attrNameToCaption, requestContext);

        if (!result.isValid) {
            ErrorHandler.instance.handleError(new InvalidConditionException(result.invalidValues));
        }

        return result.validCondition;
    }
}

export interface IFilterData {
    attrs: IFilterAttrData[];

    enabled?: boolean;
    componentId?: string;
    advancedCondition?: string;
}

export class IFilterData extends IBaseDynamicSchemaInterfaceClass<IFilterData> {
    public static readonly requiredFields: (IFieldType<IFilterData>)[] = [
        { name: "attrs", type: FieldType.object },
    ];

    public static readonly optionalFields: (IFieldType<IFilterData>)[] = [
        { name: "enabled", type: FieldType.boolean },
        { name: "componentId", type: FieldType.string },
        { name: "advancedCondition", type: FieldType.string },
    ];
}

export interface IFilterAttrData {
    attrName: string;
    conditions: IFilterAttrCondition[];

    operator?: string;
    enabled?: boolean;
    displayFormat?: string;
}

export class IFilterAttrData extends IBaseDynamicSchemaInterfaceClass<IFilterAttrData> {
    public static readonly requiredFields: (IFieldType<IFilterAttrData>)[] = [
        { name: "attrName", type: FieldType.string },
        { name: "conditions", type: FieldType.array },
    ];

    public static readonly optionalFields: (IFieldType<IFilterAttrData>)[] = [
        { name: "enabled", type: FieldType.boolean },
        { name: "operator", type: FieldType.string, allowedValues: ["and", "or"] },
        { name: "displayFormat", type: FieldType.string },
    ];
}

export interface IFilterAttrCondition {
    operation: string;
    values: (string | object | [])[]; // TODO: půjde víc specifikovat typ?

    operator?: string;
    subOperation?: string;
    enabled?: boolean;
    isMultiItem?: boolean;
    isValid?: boolean;
}

export class IFilterAttrCondition extends IBaseDynamicSchemaInterfaceClass<IFilterAttrCondition> {
    public static readonly requiredFields: (IFieldType<IFilterAttrCondition>)[] = [
        { name: "operation", type: FieldType.string },
        { name: "values", type: FieldType.array },
    ];

    public static readonly optionalFields: (IFieldType<IFilterAttrCondition>)[] = [
        { name: "subOperation", type: FieldType.string },
        { name: "enabled", type: FieldType.boolean },
        { name: "isMultiItem", type: FieldType.boolean },
        { name: "isValid", type: FieldType.boolean },
        { name: "operator", type: FieldType.string, allowedValues: ["and", "or"] },
    ];
}

export interface IFilterAttrConditionObjectValue {
    value: any;

    userValue?: any;
    additional?: any;
    fieldId?: string;
    fieldType?: string;
    subOperationId?: string;
}

export class IFilterAttrConditionObjectValue extends IBaseDynamicSchemaInterfaceClass<IFilterAttrConditionObjectValue> {
    public static readonly requiredFields: (IFieldType<IFilterAttrConditionObjectValue>)[] = [
        { name: "value", type: FieldType.any },
    ];

    public static readonly optionalFields: (IFieldType<IFilterAttrConditionObjectValue>)[] = [
        { name: "userValue", type: FieldType.any },
        { name: "additional", type: FieldType.any },
        { name: "fieldId", type: FieldType.string },
        { name: "fieldType", type: FieldType.string },
        { name: "subOperationId", type: FieldType.string },
    ];
}
